import { Component } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { EMPTY, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { Contact } from 'src/app/model/contact.class';
import { environment } from 'src/environments/environment';

import { SubscriptionService } from '../shell/subscription.service';
import {
  APIAddressbookFilter,
  APIAddressbookRecord,
  APIAddressbookSearchProperties,
  ContactSearchLabel,
  ContactSearchOptions,
  DEFAULT_CONTACTS_PAGE_SIZE,
  PaginatedAPIAddressbookContacts,
  STARTING_PAGE,
  TABS,
} from './contact-list.model';
import { ContactListService } from './contact-list.service';

@Component({
  selector: 'app-contact-list',
  templateUrl: './contact-list.component.html',
  styleUrls: ['./contact-list.component.scss'],
})
export class ContactListComponent {
  contactLists = this.contactListService.currentContactList;
  letters: string[] = [];
  lists: object = {};

  public filteredList = new Map(this.contactLists);
  public filteredListSize: number = this.countFilteredListItems();

  public form: FormGroup;
  public inputValueChangeSub: Subscription;

  // start tab variables
  public TABS = TABS;
  public currentTab: string = TABS.INTERNALS;
  public isSharedAvailable: boolean = this.subscriptionService.currentSubscription.addons.some((v) =>
    v.id.includes('address_book')
  );
  // end tab

  // start shared phone book variables
  public currentPageSize = DEFAULT_CONTACTS_PAGE_SIZE;
  public currentPage = STARTING_PAGE;
  public sharedPhonebook: PaginatedAPIAddressbookContacts;
  public currentFilters: APIAddressbookFilter = {};
  public loadingSharedContacts = false;
  public fakeValues = new Array(DEFAULT_CONTACTS_PAGE_SIZE);
  public fetchError = false;
  public selectedContact: APIAddressbookRecord;
  public showContactDetails = false;
  public remoteSearch$: Subject<{
    filters: APIAddressbookFilter;
    page: number;
    size: number;
  }> = new Subject();

  // end shared phone book variables
  private remoteSearchSub: Subscription;
  public searchOptions: ContactSearchOptions[] = [
    {
      label: ContactSearchLabel.FULL_NAME,
      icon: 'icon-small-user',
    },
    {
      label: ContactSearchLabel.PHONE_NUMBER,
      icon: 'icon-small-handset',
    },
  ];

  public selectedContactSearchOption: ContactSearchOptions = this.contactListService.loadSearchOptionFromStorage(
    this.searchOptions
  );

  public phoneCountryCode = 'it';

  constructor(
    private contactListService: ContactListService,
    private sanitizer: DomSanitizer,
    private subscriptionService: SubscriptionService
  ) {
    const iterator = this.contactLists.keys();
    let key = iterator.next().value;
    while (key) {
      this.letters.push(key);
      this.lists[key] = this.contactLists.get(key);
      key = iterator.next().value;
    }
  }

  ngOnInit() {
    this.createForm();
    this.setupSearchSubscription();

    this.remoteSearchSub = this.remoteSearch$
      .pipe(
        tap(() => {
          this.loadingSharedContacts = true;
          this.fetchError = false;
        }),
        switchMap((values: { filters: APIAddressbookFilter; page: number; size: number }) => {
          return this.getSharedContacts(values.filters, values.page, values.size).pipe(
            tap(() => {
              this.loadingSharedContacts = false;
            }),
            catchError(() => {
              this.fetchError = true;
              this.loadingSharedContacts = false;

              return EMPTY;
            })
          );
        })
      )
      .subscribe((res: PaginatedAPIAddressbookContacts) => {
        this.sharedPhonebook = res;
        this.fetchError = false;
      });
  }

  ngOnDestroy() {
    this.inputValueChangeSub?.unsubscribe();
    this.remoteSearchSub?.unsubscribe();
  }

  sanitize(extension: string) {
    return this.sanitizer.bypassSecurityTrustUrl(`${environment.CALL_PREFIX}${extension}?dialAction=autoCall`);
  }

  // start tab functions
  public onChangeTab(tab: string) {
    if (tab && tab !== this.currentTab) {
      this.currentTab = tab;
      this.form.controls.searchInput.setValue('', { emitEvent: false });
      this.currentFilters = {};
      if (this.currentTab === TABS.SHARED) {
        this.remoteSearch$.next({
          filters: {},
          page: STARTING_PAGE,
          size: DEFAULT_CONTACTS_PAGE_SIZE,
        });
      }
      if (this.currentTab === TABS.INTERNALS && this.showContactDetails) {
        this.showContactDetails = false;
      }
    }
  }
  // end tab functions

  // start shared phone book functions
  public onGoNextPage() {
    if (!this.sharedPhonebook || this.sharedPhonebook.last) return;
    this.remoteSearch$.next({
      filters: this.currentFilters,
      page: this.sharedPhonebook.number + 1,
      size: this.sharedPhonebook.size,
    });
  }

  public onGoPrevPage() {
    if (!this.sharedPhonebook || this.sharedPhonebook.first) return;
    this.remoteSearch$.next({
      filters: this.currentFilters,
      page: this.sharedPhonebook.number - 1,
      size: this.sharedPhonebook.size,
    });
  }

  public onRetryFetchContact() {
    if (!this.sharedPhonebook) return;
    this.remoteSearch$.next({
      filters: this.currentFilters,
      page: this.sharedPhonebook.number,
      size: this.sharedPhonebook.size,
    });
  }

  private getSharedContacts(filters: APIAddressbookFilter, page: number, size: number) {
    return this.contactListService.getSharedContacts({
      ...filters,
      page,
      size,
    });
  }

  public openContactDetails(contact: APIAddressbookRecord) {
    this.selectedContact = contact;
    this.showContactDetails = true;
  }
  // end shared phone book functions

  private internalsSearch() {
    this.contactLists.forEach((value: Contact[], key: string) => {
      this.filteredList.set(
        key,
        value.filter((value: Contact) => {
          return (
            `${value.firstName} ${value.lastName}`.includes(this.currentFilters.input) ||
            value.extension.includes(this.currentFilters.input)
          );
        })
      );
      this.filteredListSize = this.countFilteredListItems();
    });
  }

  private countFilteredListItems(): number {
    let count = 0;
    this.filteredList.forEach((v) => {
      count += v.length;
    });

    return count;
  }

  public selectContactSearchOption(option: ContactSearchOptions): void {
    if (this.selectedContactSearchOption.label === option.label) {
      return;
    }

    this.selectedContactSearchOption = option;
    this.contactListService.saveSearchOptionToStorage(option);

    this.inputValueChangeSub?.unsubscribe();
    this.createForm();
    this.setupSearchSubscription();
  }

  private createForm(): void {
    this.form = new FormGroup({
      searchInput: new FormControl(''),
    });
  }

  private setupSearchSubscription(): void {
    this.inputValueChangeSub = this.form.controls.searchInput.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((newValue: string | null) => {
        if (this.currentTab === TABS.INTERNALS) {
          this.currentFilters = { input: newValue };
          this.internalsSearch();
        }

        if (this.currentTab === TABS.SHARED) {
          this.currentFilters = {
            input: newValue,
            properties: this.getSearchProperties(),
          };
          this.remoteSearch$.next({
            filters: this.currentFilters,
            page: STARTING_PAGE,
            size: DEFAULT_CONTACTS_PAGE_SIZE,
          });
        }
      });
  }

  private getSearchProperties(): APIAddressbookSearchProperties[] {
    if (this.selectedContactSearchOption.label === ContactSearchLabel.PHONE_NUMBER) {
      return [APIAddressbookSearchProperties.PHONE_NUMBER];
    }

    return [APIAddressbookSearchProperties.FIRST_NAME, APIAddressbookSearchProperties.LAST_NAME];
  }

  public get isPhoneNumberSearch(): boolean {
    return this.selectedContactSearchOption.label === ContactSearchLabel.PHONE_NUMBER;
  }

  public get isFullNameSearch(): boolean {
    return this.selectedContactSearchOption.label === ContactSearchLabel.FULL_NAME;
  }
}
